/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-07-10 15:17:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import {
  TRIGGER_SYNC_REQUEST,
  TRIGGER_SYNC_SUCCESS,
  TRIGGER_SYNC_FAILURE,
  GET_SYNC_STATUS_REQUEST,
  GET_SYNC_STATUS_SUCCESS,
  GET_SYNC_STATUS_FAILURE,
  GET_SYNC_PROCESS_STATUS_REQUEST,
  GET_SYNC_PROCESS_STATUS_SUCCESS,
  GET_SYNC_PROCESS_STATUS_FAILURE,
  GET_AVAILABLE_SYNC_LIST_REQUEST,
  GET_AVAILABLE_SYNC_LIST_SUCCESS,
  GET_AVAILABLE_SYNC_LIST_FAILURE,
  GET_SYNC_SCHEDULE_REQUEST,
  GET_SYNC_SCHEDULE_SUCCESS,
  GET_SYNC_SCHEDULE_FAILURE,
  SAVE_SYNC_SCHEDULE_REQUEST,
  SAVE_SYNC_SCHEDULE_SUCCESS,
  SAVE_SYNC_SCHEDULE_FAILURE,
  GET_SYNC_HISTORY_REQUEST,
  GET_SYNC_HISTORY_SUCCESS,
  GET_SYNC_HISTORY_FAILURE,
  RESET_SYNC_STATUS,
  ENABLE_AUTO_SYNC_REQUEST,
  ENABLE_AUTO_SYNC_SUCCESS,
  ENABLE_AUTO_SYNC_FAILURE,
  UPDATE_SYNC_PRIORITY_REQUEST,
  UPDATE_SYNC_PRIORITY_SUCCESS,
  UPDATE_SYNC_PRIORITY_FAILURE
} from 'stores/flow-type/sync.type';

import type { SyncStateType, SyncActionType } from 'stores/flow-type/sync.type';

const initialState: SyncStateType = {
  triggerSyncLoading: false,
  triggerSyncSuccess: false,
  response: {},
  error: {},
  syncLoading: false,
  lastSyncedDoneAt: 0,
  syncProcessLoading: false,
  flow: { status: 'done' },
  scheduleSyncInfo: {},
  syncProcess: {},
  autoSyncStatus: {},
  getAvailableSyncListLoading: false,
  getAvailableSyncListSuccess: false,
  availableSyncList: [],
  collectionBoostAllExists: false,
  getSyncScheduleLoading: false,
  saveSyncScheduleLoading: false,
  syncSchedule: {},
  getSyncHistoryLoading: false,
  syncHistory: {},
  syncHistoryTotal: 0,
  enableSyncLoading: false,
  enableSyncSuccess: false,
  updateSyncPriorityLoading: false,
  updateSyncPriorityError: false
};

const syncReducers = (state: SyncStateType = initialState, action: SyncActionType) => {
  switch (action.type) {
    case TRIGGER_SYNC_REQUEST:
      return {
        ...state,
        triggerSyncLoading: true
      };
    case TRIGGER_SYNC_SUCCESS:
      return {
        ...state,
        triggerSyncLoading: false,
        triggerSyncSuccess: true,
        response: action.payload
      };
    case TRIGGER_SYNC_FAILURE:
      return {
        ...state,
        triggerSyncLoading: false,
        triggerSyncSuccess: false,
        error: action.payload
      };

    case GET_SYNC_STATUS_REQUEST:
      return { ...state, syncLoading: true };
    case GET_SYNC_STATUS_SUCCESS: {
      const {
        data: { lastSynced = {} }
      } = action.payload;
      const { doneAt = 0 } = lastSynced;
      return {
        ...state,
        syncLoading: false,
        error: undefined,
        lastSyncedDoneAt: doneAt
      };
    }
    case GET_SYNC_STATUS_FAILURE:
      return {
        ...state,
        syncLoading: false,
        success: false,
        error: action.payload
      };

    case GET_SYNC_PROCESS_STATUS_REQUEST:
      return { ...state, syncProcessLoading: true };
    case GET_SYNC_PROCESS_STATUS_SUCCESS: {
      const { data = {} } = action.payload;
      let { flow, schedule: scheduleSyncInfo, process: syncProcess, autoSyncStatus = {} } = data;
      if (_.isEmpty(flow)) {
        flow = { status: 'done' };
      }

      return {
        ...state,
        syncProcessLoading: false,
        flow,
        scheduleSyncInfo,
        syncProcess,
        autoSyncStatus
      };
    }
    case GET_SYNC_PROCESS_STATUS_FAILURE:
      return {
        ...state,
        syncProcessLoading: false,
        success: false,
        error: action.payload
      };
    case GET_AVAILABLE_SYNC_LIST_REQUEST:
      return { ...state, getAvailableSyncListLoading: true };
    case GET_AVAILABLE_SYNC_LIST_SUCCESS: {
      const { data } = action.payload;
      const { available = [], info = {} } = data;
      const { collectionBoostAllExists = false } = info;
      return {
        ...state,
        getAvailableSyncListLoading: false,
        getAvailableSyncListSuccess: true,
        availableSyncList: available,
        collectionBoostAllExists
      };
    }
    case GET_AVAILABLE_SYNC_LIST_FAILURE:
      return {
        ...state,
        getAvailableSyncListLoading: false,
        getAvailableSyncListSuccess: false,
        error: action.payload
      };
    case GET_SYNC_SCHEDULE_REQUEST:
      return { ...state, getSyncScheduleLoading: true };
    case GET_SYNC_SCHEDULE_SUCCESS: {
      const { data = {} } = action.payload;
      const { schedule = {} } = data;
      return {
        ...state,
        getSyncScheduleLoading: false,
        syncSchedule: schedule
      };
    }
    case GET_SYNC_SCHEDULE_FAILURE:
      return {
        ...state,
        getSyncScheduleLoading: false,
        error: action.payload
      };
    case SAVE_SYNC_SCHEDULE_REQUEST:
      const { flowName } = action.payload;
      const isRemoveSyncSchedule = _.isEmpty(flowName);
      return { ...state, saveSyncScheduleLoading: true, isRemoveSyncSchedule };
    case SAVE_SYNC_SCHEDULE_SUCCESS: {
      const { data = {} } = action.payload;
      const { schedule = {} } = data;
      return {
        ...state,
        saveSyncScheduleLoading: false,
        syncSchedule: schedule
      };
    }
    case SAVE_SYNC_SCHEDULE_FAILURE:
      return {
        ...state,
        saveSyncScheduleLoading: false,
        error: action.payload
      };
    case GET_SYNC_HISTORY_REQUEST:
      return { ...state, getSyncHistoryLoading: true, syncHistory: [], syncHistoryTotal: 0 };
    case GET_SYNC_HISTORY_SUCCESS: {
      const { data: result = {} } = action.payload;
      const { data = {}, total } = result;
      return {
        ...state,
        getSyncHistoryLoading: false,
        syncHistory: data,
        syncHistoryTotal: total
      };
    }
    case GET_SYNC_HISTORY_FAILURE:
      return {
        ...state,
        getSyncHistoryLoading: false,
        error: action.payload
      };
    case RESET_SYNC_STATUS:
      return initialState;
    case ENABLE_AUTO_SYNC_REQUEST:
      return {
        ...state,
        enableSyncLoading: true
      };
    case ENABLE_AUTO_SYNC_SUCCESS:
      return {
        ...state,
        enableSyncLoading: false,
        enableSyncSuccess: true
      };
    case ENABLE_AUTO_SYNC_FAILURE:
      return {
        ...state,
        error: action.payload
      };
    case UPDATE_SYNC_PRIORITY_REQUEST:
      return { ...state, updateSyncPriorityLoading: true };
    case UPDATE_SYNC_PRIORITY_SUCCESS: {
      return {
        ...state,
        updateSyncPriorityLoading: false
      };
    }
    case UPDATE_SYNC_PRIORITY_FAILURE:
      return {
        ...state,
        updateSyncPriorityLoading: false,
        updateSyncPriorityError: true,
        error: action.payload
      };
    default:
      return state;
  }
};

export default syncReducers;
